import { useCallback, useEffect, useMemo, useRef } from 'react'
import { checkpointToShapes } from '@/lib/checkpoint/checkpoint-to-shapes'
import { computeReorderedFractionalIndexes } from '@/lib/elements/stowed-fractional-index'
import {
  insertOrderedIntoUnifiedLayer,
  liveLayerOrder,
  mergeLiveLayerOrder,
} from '@/lib/layout/layer-order'
import { reconcileClikArtPlacementsWithShapes } from '@/lib/clikart/sync-clikart-placement-from-shapes'
import { observePhotoPlacementsFromShapes } from '@/lib/photos/sync-photo-placement-from-shapes'
import { useClikArtPlacementRefresh } from '@/hooks/use-clikart-placement-refresh'
import { syncTemplatePlacementsWithShapes } from '@/lib/templates/sync-template-placement-from-shapes'
import { activateTemplateSpread } from '@/lib/templates/template-placement-session'
import { useResolveMissingImagePreviews } from '@/hooks/use-resolve-missing-image-previews'
import type { StowedElement } from '@/api/types/stowed-element'
import { elementsService } from '@/api/services/elements.service'
import { logger } from '@/utils/logger'
import { buildStowedShapeElement } from '../../helpers/build-stowed-shape-element'
import { extractPageTopics } from '../../helpers/page-topics'
import type {
  HomeDocumentLayout,
  HomeFoundation,
  HomePersistence,
  HomeShapePersistence,
  HomeZoomAndSpread,
} from '../../types/home-groups'

/**
 * What the canvas renders from the loaded checkpoint: shape mapping with missing-preview backfill,
 * page titles, the initial unified layer order, the z-index map and Arrange commands, and logging
 * a failed checkpoint load.
 */
export function useHomeCheckpointView(
  deps: Pick<
    HomeFoundation,
    | 'unifiedLayerIds'
    | 'setUnifiedLayerIds'
    | 'shapeManagerRef'
    | 'shapeTextBakeRef'
    | 'fontLookupRef'
    | 'unifiedLayerIdsRef'
  > &
    Pick<HomeZoomAndSpread, 'data' | 'error' | 'activeSpread' | 'spreadId'> &
    Pick<
      HomeDocumentLayout,
      'hasLoadedBookSettings' | 'spreadProjection' | 'resolvedLayout' | 'spreadPlacementBounds'
    > &
    Pick<HomePersistence, 'pushAction' | 'markEdited'> &
    Pick<HomeShapePersistence, 'handleShapeCommitted'>
) {
  const {
    unifiedLayerIds,
    setUnifiedLayerIds,
    shapeManagerRef,
    shapeTextBakeRef,
    fontLookupRef,
    unifiedLayerIdsRef,
    data,
    error,
    activeSpread,
    spreadId,
    hasLoadedBookSettings,
    spreadProjection,
    resolvedLayout,
    spreadPlacementBounds,
    pushAction,
    markEdited,
    handleShapeCommitted,
  } = deps

  const checkpointMapping = useMemo(() => {
    if (!data?.metadata || !hasLoadedBookSettings) return null

    return checkpointToShapes(data.metadata, spreadProjection)
  }, [data?.metadata, hasLoadedBookSettings, spreadProjection])

  const checkpointShapes = checkpointMapping?.shapes
  const checkpointDiagnostics = checkpointMapping?.diagnostics ?? null

  // Legacy NSD (and our pre-fix save format) never persisted a preview URL for placed photos —
  // only an image id. Backfill it from the photos API so these render instead of showing broken.
  useResolveMissingImagePreviews({
    spreadId,
    shapes: checkpointShapes,
    shapeManagerRef,
    onShapeCommitted: handleShapeCommitted,
  })

  // Seed the ClikArt (and photo) "Placed" tracking from each spread as it first loads, so a ClikArt placed in
  // an earlier session is known to this session and clears its badge once its last instance is
  // removed. Once per spread per session: a later load of a cached checkpoint must not override live edits.
  const clikArtScannedSpreadIdsRef = useRef(new Set<string>())

  useEffect(() => {
    if (!checkpointShapes || !spreadId) return
    if (resolvedLayout.bleedWidth <= 0 || resolvedLayout.bleedHeight <= 0) return
    if (clikArtScannedSpreadIdsRef.current.has(spreadId)) return

    clikArtScannedSpreadIdsRef.current.add(spreadId)
    reconcileClikArtPlacementsWithShapes(checkpointShapes, spreadPlacementBounds, spreadId)
    // Photos too: the Photos panel's Placed field needs to know which pages of this spread each
    // photo sits on, so a photo placed in an earlier session drops its page once it is moved off
    // or removed. Observed, not tracked: the photo may be on spreads this session never opens.
    observePhotoPlacementsFromShapes(checkpointShapes, spreadPlacementBounds, spreadId)
  }, [
    spreadPlacementBounds,
    checkpointShapes,
    spreadId,
    resolvedLayout.bleedWidth,
    resolvedLayout.bleedHeight,
  ])

  // The Templates gallery highlights the template most recently placed on the spread on screen.
  // The session remembers a spread's placements across visits, but the elements may have changed
  // while it was off screen (a collaborator deleted them), so each visit re-syncs once from the
  // shapes the spread loaded with, which are what the canvas shows. Later edits keep it current.
  const templateSyncedSpreadIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!spreadId) return

    templateSyncedSpreadIdRef.current = null
    activateTemplateSpread(spreadId)
  }, [spreadId])

  useEffect(() => {
    if (!checkpointShapes || !spreadId) return
    if (templateSyncedSpreadIdRef.current === spreadId) return

    templateSyncedSpreadIdRef.current = spreadId
    syncTemplatePlacementsWithShapes(checkpointShapes, spreadId)
  }, [checkpointShapes, spreadId])

  useClikArtPlacementRefresh()

  const pageTitles = useMemo(() => {
    const fromCheckpoint = extractPageTopics(data?.metadata)
    const pages = activeSpread?.pages

    if (!pages?.length) return fromCheckpoint

    const sorted = [...pages].sort((a, b) => (a.number ?? 0) - (b.number ?? 0))

    if (sorted.length === 1) {
      const p = sorted[0]
      const topic = typeof p.pageTopic === 'string' ? p.pageTopic.trim() : ''

      if (p.leftPage === true) {
        return { left: topic || fromCheckpoint.left, right: fromCheckpoint.right }
      }

      return { left: fromCheckpoint.left, right: topic || fromCheckpoint.right }
    }

    const leftP = sorted.find((p) => p.leftPage === true) ?? sorted[0]
    const rightP = sorted.find((p) => p.leftPage === false) ?? sorted[1]
    const lt = typeof leftP?.pageTopic === 'string' ? leftP.pageTopic.trim() : ''
    const rt = typeof rightP?.pageTopic === 'string' ? rightP.pageTopic.trim() : ''

    return {
      left: lt || fromCheckpoint.left,
      right: rt || fromCheckpoint.right,
    }
  }, [activeSpread, data?.metadata])

  const didInitialUnifiedOrderRef = useRef(false)
  const lastUnifiedLoadKeyRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (lastUnifiedLoadKeyRef.current !== spreadId) {
      lastUnifiedLoadKeyRef.current = spreadId
      didInitialUnifiedOrderRef.current = false
    }

    if (!checkpointShapes) return
    if (didInitialUnifiedOrderRef.current) return
    const all = checkpointShapes.map((s) => ({ id: s.id, fi: s.fractionalIndex }))
    const withFi = all
      .filter((x) => x.fi != null)
      .sort((a, b) => (a.fi! < b.fi! ? -1 : a.fi! > b.fi! ? 1 : 0))
    const withoutFi = all.filter((x) => x.fi == null)

    const orderedIds = [...withFi, ...withoutFi].map((x) => x.id)

    unifiedLayerIdsRef.current = orderedIds
    setUnifiedLayerIds(orderedIds)
    didInitialUnifiedOrderRef.current = true
  }, [checkpointShapes, spreadId, setUnifiedLayerIds, unifiedLayerIdsRef])

  const elementZIndexMap = useMemo(() => {
    const map = new Map<string, number>()

    unifiedLayerIds.forEach((id, i) => map.set(id, i + 1))
    return map
  }, [unifiedLayerIds])

  const handleUnifiedLayerArrange = useCallback(
    (
      selectedIds: string[],
      op: 'bringToFront' | 'bringForward' | 'sendBackward' | 'sendToBack' | 'insertOrdered',
      opts?: { historyTxnId?: string }
    ) => {
      const prev = unifiedLayerIdsRef.current

      // Expand to include all group descendants so children of selected groups
      // (shapes and text boxes inside a group) move with the group.
      const allShapes = shapeManagerRef.current?.getShapes() ?? []
      const sel = new Set(selectedIds)
      let changed = true

      while (changed) {
        changed = false
        for (const shape of allShapes) {
          if (shape.parentGroupId && sel.has(shape.parentGroupId) && !sel.has(shape.id)) {
            sel.add(shape.id)
            changed = true
          }
        }
      }

      // Arrange steps over deleted objects, whose ids stay in the order (see `liveLayerOrder`):
      // otherwise one Send backward could move the object behind a deleted id and change nothing
      // visible (WMPCM-12998). Their slots are kept, so undo still restores a delete in place.
      const live = liveLayerOrder(prev, shapeManagerRef.current?.getShapes())
      let newOrder: string[]

      switch (op) {
        case 'bringToFront':
          newOrder = [...live.filter((id) => !sel.has(id)), ...live.filter((id) => sel.has(id))]
          break
        case 'bringForward': {
          const next = [...live]

          for (let i = next.length - 2; i >= 0; i--) {
            if (sel.has(next[i]!) && !sel.has(next[i + 1]!)) {
              ;[next[i], next[i + 1]] = [next[i + 1]!, next[i]!]
            }
          }

          newOrder = next
          break
        }

        case 'sendBackward': {
          const next = [...live]

          for (let i = 1; i < next.length; i++) {
            if (sel.has(next[i]!) && !sel.has(next[i - 1]!)) {
              ;[next[i - 1], next[i]] = [next[i]!, next[i - 1]!]
            }
          }

          newOrder = next
          break
        }

        case 'sendToBack':
          newOrder = [...live.filter((id) => sel.has(id)), ...live.filter((id) => !sel.has(id))]
          break
        case 'insertOrdered':
          // For freshly created content (e.g. clipboard paste), `selectedIds` isn't a selection to
          // reorder relative to itself — it's the exact desired relative order. Unlike
          // 'bringToFront', which preserves whatever order the selection already happens to be
          // in, this uses the given order directly.
          newOrder = insertOrderedIntoUnifiedLayer(prev, selectedIds, sel)
          break

        default:
          return
      }

      if (op !== 'insertOrdered') newOrder = mergeLiveLayerOrder(prev, newOrder)

      if (newOrder.join(',') === prev.join(',')) return

      logger.warn('[unifiedLayerIds] new order after', op, ':', newOrder)
      unifiedLayerIdsRef.current = newOrder
      setUnifiedLayerIds(newOrder)

      // Compute FI changes in unified (cross-type) context so that shapes and text boxes
      // are assigned fractionalIndex values that interleave correctly when the spread reloads.
      // The type-scoped handlers (handleLayerReorder / handleTextLayerReorder) are no-ops;
      // all FI persistence, undo history, and markEdited live here.
      const shapeById = new Map(allShapes.map((s) => [s.id, s]))
      const getFI = (id: string): string | undefined => shapeById.get(id)?.fractionalIndex

      const newFIs = computeReorderedFractionalIndexes(newOrder, getFI)

      const beforeFIChanges: Record<string, string> = {}
      const fiChanges: Record<string, string> = {}
      const resequenceStubs: StowedElement[] = []

      for (const [id, newFI] of newFIs) {
        const shape = shapeById.get(id)

        if (shape) {
          beforeFIChanges[id] = shape.fractionalIndex ?? ''
          fiChanges[id] = newFI
          shapeManagerRef.current?.updateShape(id, { fractionalIndex: newFI })
          resequenceStubs.push(
            buildStowedShapeElement(
              shapeTextBakeRef.current,
              { ...shape, fractionalIndex: newFI },
              undefined,
              spreadProjection,
              fontLookupRef.current ?? undefined
            )
          )
        }
      }

      if (resequenceStubs.length > 0) {
        void elementsService.resequenceElements(spreadId, 'userLayer', resequenceStubs)
      }

      pushAction({
        type: 'unifiedReorder',
        beforeOrder: prev,
        afterOrder: newOrder,
        fiChanges: Object.keys(fiChanges).length > 0 ? fiChanges : undefined,
        beforeFIChanges: Object.keys(beforeFIChanges).length > 0 ? beforeFIChanges : undefined,
        historyTxnId: opts?.historyTxnId,
      })

      void markEdited([...sel])
    },
    [
      markEdited,
      pushAction,
      spreadId,
      spreadProjection,
      fontLookupRef,
      setUnifiedLayerIds,
      shapeManagerRef,
      shapeTextBakeRef,
      unifiedLayerIdsRef,
    ]
  )

  // Only log errors if the query fails
  useEffect(() => {
    if (error) {
      console.error('❌ Error loading spread checkpoint:', error)
    }
  }, [error])

  return {
    checkpointMapping,
    checkpointShapes,
    checkpointDiagnostics,
    pageTitles,
    didInitialUnifiedOrderRef,
    lastUnifiedLoadKeyRef,
    elementZIndexMap,
    handleUnifiedLayerArrange,
  }
}
